import React, {useState} from 'react';
import {View, Text, TouchableOpacity} from 'react-native';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import styles, {infoIconStyle} from '../../../style/adminTools';
import TooltipBubble from '../components/TooltipBubble';
import useLayoutTier from '../components/useLayoutTier';
import useSettingSave from '../components/useSettingSave';
import HighlightedLabel from '../components/HighlightedLabel';

const CompositeSettingRow = ({
  settingKey,
  label,
  tooltip,
  value,
  onSave,
  highlight = false,
  highlightQuery,
  disabled = false,
  onSaveStateChange,
  children
}) => {
  const stacked = useLayoutTier() === 'mobile';
  const [showTooltip, setShowTooltip] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const {draft, pending, error, save} = useSettingSave({
    settingKey,
    value,
    onSave,
    onSaveStateChange
  });
  const id = `setting-${settingKey}`;
  const blocked = disabled || pending;
  const shownError = validationError || error;

  return (
    <View
      style={[
        styles.settingRow,
        stacked ? styles.settingRowPhone : null,
        highlight ? styles.settingRowMatch : null
      ]}
      testID={id}
    >
      {showTooltip && tooltip && stacked ? (
        <TooltipBubble full text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      <View
        style={[
          styles.settingRowMain,
          stacked ? styles.settingRowMainStacked : null
        ]}
        testID={`${id}-main`}
      >
        <View
          style={[
            styles.settingLabelWrap,
            stacked ? styles.settingLabelWrapStacked : null
          ]}
        >
          <HighlightedLabel
            text={label}
            query={highlightQuery}
            style={styles.settingLabel}
            testID={`${id}-label`}
          />
          {tooltip ? (
            <TouchableOpacity
              onPress={() => setShowTooltip(!showTooltip)}
              style={[
                styles.settingInfoButton,
                stacked ? styles.settingInfoButtonPhone : null
              ]}
              accessibilityLabel={I18n.t('adminTools.aboutSetting', {label})}
              testID={`${id}-info`}
            >
              <i className="icon-info-circled" style={infoIconStyle} />
            </TouchableOpacity>
          ) : null}
          {pending ? (
            <Text style={styles.settingSavingText}>
              {I18n.t('unsavedChanges.saving')}
            </Text>
          ) : null}
        </View>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: stacked ? 'flex-start' : 'flex-end',
            gap: 8
          }}
          testID={`${id}-control`}
        >
          {children({
            draft,
            blocked,
            save,
            idPrefix: id,
            label,
            stacked,
            setValidationError
          })}
        </View>
      </View>
      {showTooltip && tooltip && !stacked ? (
        <TooltipBubble text={tooltip} testID={`${id}-tooltip`} />
      ) : null}
      {shownError ? (
        <Text style={styles.settingErrorText} testID={`${id}-error`}>
          {shownError}
        </Text>
      ) : null}
    </View>
  );
};

CompositeSettingRow.propTypes = {
  settingKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  highlight: PropTypes.bool,
  highlightQuery: PropTypes.string,
  disabled: PropTypes.bool,
  onSaveStateChange: PropTypes.func,
  children: PropTypes.func.isRequired
};

export default CompositeSettingRow;
